// The EUSDR questions as an .xlsx, built in the browser from the list the page has loaded:
// one tab for the questions, one for the conflicting clauses and one for the stale section
// references. Always the full list, whatever the page's filters.
// docs/Y2026-Q4/design-eusdr-questions.md
import type {
  EusdrQuestion,
  EusdrQuestions,
  EusdrReply,
  EusdrWords,
} from "../../api/hooks/useEusdrQuestions";
import { loadXlsxStyle } from "../../utils/xlsxStyle";
import { LETTERS, MARK_LABEL, RULINGS, rulingLabel, statusLabel, when } from "./eusdrLabels";

interface Column {
  header: string;
  width: number;
}

interface Row {
  cells: string[];
  /** A question asked through its parts, shaded above them. */
  group?: boolean;
}

const HEADER = {
  font: { bold: true, color: { rgb: "FFFFFF" } },
  fill: { fgColor: { rgb: "1F2A44" } },
  alignment: { wrapText: true, vertical: "center", horizontal: "left" },
};
const GROUP = {
  font: { bold: true },
  fill: { fgColor: { rgb: "EEF1F6" } },
  alignment: { wrapText: true, vertical: "top", horizontal: "left" },
};
const CELL = { alignment: { wrapText: true, vertical: "top", horizontal: "left" } };

/** Where the conflicts and the section references are answered, and how. */
const OTHER_TAB: Record<"conflicts" | "references", string> = {
  conflicts:
    'Answered on the "Conflicting clauses" tab. Each row there is one pair of clauses that ' +
    "require different things, with clause A and clause B side by side: what each requires, " +
    'its wording and its source. Put A or B in "Which applies (A or B)" for how it should work ' +
    'on our buildings, and anything to add in "Responses".',
  references:
    'Answered on the "Section references" tab. Each row there is one reference to a section ' +
    "by an older number: the wording that cites it, and what the current EUSDR or Exhibit 9 " +
    'holds at that number now. Put what the reference should point to in "Corrected ' +
    'reference (responses)".',
};

/** The documents' words as on the page: each line its citation then what it says, a blank
 * line between groups. */
const documentWords = (words: EusdrWords[]): string =>
  words
    .map((w, i) => {
      const line = w.citation ? `${w.citation}: ${w.text}` : w.text;
      return i > 0 && words[i - 1].group !== w.group ? `\n${line}` : line;
    })
    .join("\n");

/** "Marta (Amazon), 28 Oct 2026 at 14:05: A applies: 1.5 m. Agreed." */
const response = (q: EusdrQuestion, r: EusdrReply): string => {
  const said = [
    r.ruling ? rulingLabel(q, r.ruling) : null,
    r.marks && !r.ruling ? MARK_LABEL[r.marks] : null,
    r.text || null,
  ].filter(Boolean);
  const side = r.side === "amazon" ? "Amazon" : "InRange";
  return `${r.name} (${side}), ${when(r.at)}: ${said.join(". ")}`;
};

const responses = (q: EusdrQuestion): string => q.replies.map((r) => response(q, r)).join("\n\n");

const latestFromAmazon = (q: EusdrQuestion): string => {
  const r = [...q.replies].reverse().find((x) => x.side === "amazon");
  return r ? response(q, r) : "";
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sheet = (XLSX: any, columns: Column[], rows: Row[]) => {
  const ws = XLSX.utils.aoa_to_sheet([columns.map((c) => c.header), ...rows.map((r) => r.cells)]);
  ws["!cols"] = columns.map((c) => ({ wch: c.width }));
  columns.forEach((_, c) => {
    ws[XLSX.utils.encode_cell({ r: 0, c })].s = HEADER;
    rows.forEach((row, i) => {
      const cell = ws[XLSX.utils.encode_cell({ r: i + 1, c })];
      if (cell) cell.s = row.group ? GROUP : CELL;
    });
  });
  ws["!autofilter"] = { ref: ws["!ref"] };
  return ws;
};

export const downloadEusdrWorkbook = async (data: EusdrQuestions): Promise<void> => {
  const XLSX = await loadXlsxStyle();
  const stepTitle = new Map(data.steps.map((s) => [s.id, s.title]));
  const steps = (q: EusdrQuestion) => q.steps.map((s) => stepTitle.get(s) ?? s).join("\n");
  const answerable = (q: EusdrQuestion) => !q.group;

  const partsOf = (q: EusdrQuestion) => data.questions.filter((p) => p.parent === q.key);
  // The conflicts and the section references are answered on their own tabs; their questions
  // stay on this one, in order, saying where to go and what to fill in.
  const questions: Row[] = data.questions
    .filter((q) => q.sheet === "questions" || !q.parent)
    .map((q) => {
      const elsewhere = q.sheet === "questions" ? null : OTHER_TAB[q.sheet];
      const parts = partsOf(q);
      const answered = parts.filter((p) => p.status === "answered").length;
      return {
        group: q.group,
        cells: [
          q.label,
          q.parent ? "" : q.category,
          steps(q),
          [q.topic, q.question, elsewhere].filter(Boolean).join("\n\n"),
          q.context,
          documentWords(q.words),
          q.source,
          answerable(q)
            ? statusLabel(q.status)
            : elsewhere
              ? `${answered} of ${parts.length} answered`
              : "",
          latestFromAmazon(q),
          responses(q),
        ],
      };
    });

  const conflicts: Row[] = data.questions
    .filter((q) => q.sheet === "conflicts" && q.clauses.length === 2)
    .map((q) => {
      const [a, b] = q.clauses;
      const applies = q.ruling ? (LETTERS[RULINGS.indexOf(q.ruling)] ?? "") : "";
      return {
        cells: [
          q.label,
          q.topic,
          steps(q),
          q.context,
          a.says,
          a.text,
          a.source,
          b.says,
          b.text,
          b.source,
          applies,
          statusLabel(q.status),
          responses(q),
        ],
      };
    });

  const references: Row[] = data.questions
    .filter((q) => q.sheet === "references" && q.parent)
    .map((q) => {
      // Each part's words are the reference, then what the EUSDR or Exhibit 9 holds now.
      const [cites, ...now] = q.words;
      return {
        cells: [
          q.label,
          q.topic,
          cites ? documentWords([cites]) : "",
          now.map((w) => w.text).join("\n"),
          q.source,
          responses(q),
          statusLabel(q.status),
        ],
      };
    });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    wb,
    sheet(
      XLSX,
      [
        { header: "#", width: 7 },
        { header: "Category", width: 18 },
        { header: "Process step", width: 20 },
        { header: "Question", width: 50 },
        { header: "Why we are asking", width: 50 },
        { header: "What the documents say", width: 70 },
        { header: "Source", width: 28 },
        { header: "Status", width: 14 },
        { header: "Latest from Amazon", width: 50 },
        { header: "All responses", width: 60 },
      ],
      questions,
    ),
    "Questions",
  );
  XLSX.utils.book_append_sheet(
    wb,
    sheet(
      XLSX,
      [
        { header: "#", width: 7 },
        { header: "Topic", width: 30 },
        { header: "Process step", width: 18 },
        { header: "Why we are asking", width: 45 },
        { header: "Clause A requires", width: 22 },
        { header: "Clause A wording", width: 50 },
        { header: "Clause A source", width: 20 },
        { header: "Clause B requires", width: 22 },
        { header: "Clause B wording", width: 50 },
        { header: "Clause B source", width: 20 },
        { header: "Which applies (A or B)", width: 16 },
        { header: "Status", width: 14 },
        { header: "Responses", width: 60 },
      ],
      conflicts,
    ),
    "Conflicting clauses",
  );
  XLSX.utils.book_append_sheet(
    wb,
    sheet(
      XLSX,
      [
        { header: "#", width: 7 },
        { header: "Reference", width: 45 },
        { header: "Wording that cites the old section", width: 60 },
        { header: "What the current EUSDR or Exhibit 9 holds there", width: 55 },
        { header: "Source", width: 28 },
        { header: "Corrected reference (responses)", width: 60 },
        { header: "Status", width: 14 },
      ],
      references,
    ),
    "Section references",
  );
  const today = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `EUSDR questions - InRange - ${today}.xlsx`);
};
